import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import type { Invoice, InvoiceSummary } from '@/lib/invoices';

export const caseInvoiceKey = (caseId: number) => ['case-invoice', caseId] as const;
export const adminInvoiceKey = (invoiceId: number) => ['admin-invoice', invoiceId] as const;

/** Merchant view: the invoice of a murabaha case, `null` while none exists yet (404). */
export function useCaseInvoice(caseId: number | null | undefined) {
  return useQuery({
    queryKey: caseInvoiceKey(caseId ?? 0),
    enabled: !!caseId,
    refetchInterval: 20000,
    queryFn: async (): Promise<Invoice | null> => {
      const { data, error, response } = await apiClient.GET('/murabaha/cases/{case}/invoice', {
        params: { path: { case: caseId as number } },
      });
      if (response.status === 404) return null;
      if (error) throw error;
      return (data as unknown as { data: Invoice }).data;
    },
  });
}

export function useAdminInvoice(invoiceId: number | null | undefined) {
  return useQuery({
    queryKey: adminInvoiceKey(invoiceId ?? 0),
    enabled: !!invoiceId,
    queryFn: async (): Promise<Invoice> => {
      const { data, error } = await apiClient.GET('/admin/invoices/{invoice}', { params: { path: { invoice: invoiceId as number } } });
      if (error) throw error;
      return (data as unknown as { data: Invoice }).data;
    },
  });
}

export type AdminInvoiceTab = 'new' | 'in_review' | 'approved' | 'rejected' | 'all';

export function useAdminInvoices(filters: { tab: AdminInvoiceTab; merchant_id?: number; search?: string; date_from?: string; date_to?: string }, enabled = true) {
  return useQuery({
    queryKey: ['admin-invoices', filters],
    enabled,
    refetchInterval: 30000,
    queryFn: async (): Promise<{ invoices: InvoiceSummary[]; pending: number }> => {
      const query = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== undefined && v !== ''));
      const { data, error } = await apiClient.GET('/admin/invoices', { params: { query: query as never } });
      if (error) throw error;
      const res = data as unknown as { data: InvoiceSummary[]; meta?: { pending?: number } };
      return { invoices: res.data, pending: res.meta?.pending ?? 0 };
    },
  });
}
